/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IBreadcrumbViewProps } from '@msdyn365-commerce-modules/breadcrumb';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { _getScreenSize } from '../utilities/display-utils';
import { IBreadcrumbProps as IBreadcrumbExtensionProps } from '../definition-extensions/breadcrumb.ext.props.autogenerated';
import MsDyn365 from '@msdyn365-commerce/core';

const BreadCrumbView: React.FC<IBreadcrumbViewProps & IBreadcrumbExtensionProps<{}>> = props => {
    const {
        root,
        links,
        backButton,
        BreadcrumbContainer,
        BreadcrumbList,
        BreadcrumbListItem,
        separator,
        data: {
            categoryPaths: { result }
        },
        context,
        config
    } = props;

    const [screenSize, setScreenSize] = useState(_getScreenSize(context));
    if (!root && !links && !backButton) {
        props.context.telemetry.error('BreadCrumb content is empty, module wont render');
        return null;
    }

    const pageUrl = props.context.request.url.requestUrl.href.toString();

    useEffect(() => {
        if (!MsDyn365.isBrowser) {
            return;
        }

        setScreenSize(_getScreenSize(props.context));

        const handleResize = () => {
            const newScreenSize = _getScreenSize(props.context);
            setScreenSize(newScreenSize);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [props.context]);

    const isPDP = config.isPdpPage;

    if (isPDP) {
        const [expanded, setExpanded] = useState<boolean>(false);
        const isMobile = screenSize === 1;

        const shouldShowBackLink = MsDyn365.isBrowser
            ? /\/\d+\.c(\/)?$/.test(document.referrer) || document.referrer.includes('search?q')
            : false;
        const categoryNames = props.data.categoryPaths.result?.map(item => item.Name) || [];
        const totalLength = categoryNames.join('').length;
        const validCharacterLength = totalLength <= 43;

        const handleExpandClick = (e: React.MouseEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();
            setExpanded(true);
        };

        const isComingFromCategoryPage = MsDyn365.isBrowser ? /\/\d+\.c(\/)?$/.test(document.referrer) : false;
        const getCategoryHierarchy = (): Array<{ RecordId?: string; Name?: string; Url?: string; Slug?: string }> | null => {
            if (!MsDyn365.isBrowser) {
                return null;
            }
            try {
                const storedHierarchy = sessionStorage.getItem('categoryHierarchy');
                if (storedHierarchy) {
                    const parsed = JSON.parse(storedHierarchy);
                    if (Array.isArray(parsed)) {
                        return parsed;
                    }
                }
            } catch (error) {
                console.error('Error parsing categoryHierarchy from sessionStorage:', error);
            }
            return null;
        };

        const categoryHierarchy = getCategoryHierarchy();
        const shouldUseCategoryHierarchy = isComingFromCategoryPage && isPDP && categoryHierarchy && categoryHierarchy.length > 0;

        const breadcrumbLinksToRender =
            shouldUseCategoryHierarchy && categoryHierarchy
                ? categoryHierarchy.map((category, index) => {
                      const isLastItem = index === categoryHierarchy.length - 1;
                      const categoryName = category?.Name ?? '';
                      const categoryUrl = category?.Url ?? '';
                      const categoryKey = category?.RecordId ?? category?.Name ?? index;

                      return (
                          <a
                              key={categoryKey}
                              href={categoryUrl}
                              className={classnames('ms-breadcrumb_link', {
                                  'ms-breadcrumb_link-current': isLastItem
                              })}
                              aria-current={isLastItem ? 'page' : undefined}
                          >
                              {categoryName}
                          </a>
                      );
                  })
                : links;
        const renderMobileLinks = () => {
            if (!breadcrumbLinksToRender || breadcrumbLinksToRender.length === 0) {
                return null;
            }

            if (!expanded && !validCharacterLength) {
                const firstLink = breadcrumbLinksToRender[0];
                const lastLink = breadcrumbLinksToRender[breadcrumbLinksToRender.length - 1];

                return (
                    <>
                        <Node {...BreadcrumbListItem} onClick={handleExpandClick}>
                            {firstLink}
                        </Node>
                        <Node {...BreadcrumbListItem}>…</Node>
                        <Node {...BreadcrumbListItem} className='breadcrumb_item-current' aria-current='page'>
                            {lastLink}
                        </Node>
                    </>
                );
            }

            return breadcrumbLinksToRender.map((link, index) => (
                <Node
                    key={index}
                    {...BreadcrumbListItem}
                    className={classnames(
                        BreadcrumbListItem.className,
                        index === breadcrumbLinksToRender.length - 1 ? 'breadcrumb_item-current' : ''
                    )}
                >
                    {link}
                    {index < breadcrumbLinksToRender.length - 1 && separator}
                </Node>
            ));
        };

        const renderDesktopLinks = () => {
            if (!breadcrumbLinksToRender || breadcrumbLinksToRender.length === 0 || isMobile) {
                return null;
            }

            return breadcrumbLinksToRender.map((link, index) => (
                <Node
                    key={index}
                    {...BreadcrumbListItem}
                    className={classnames(
                        BreadcrumbListItem.className,
                        index === breadcrumbLinksToRender.length - 1 ? 'breadcrumb_item-current' : ''
                    )}
                >
                    {link}
                    {index < breadcrumbLinksToRender.length - 1 && separator}
                </Node>
            ));
        };

        return (
            <Module
                {...BreadcrumbContainer}
                className={`ms-breadcrumb ${!expanded && !validCharacterLength && isMobile ? 'breadcrumb-collapsed' : ''}`}
            >
                {shouldShowBackLink && (
                    <button onClick={() => history.back()} className='breadcrumb-back-button'>
                        Back to results
                    </button>
                )}
                <Node {...BreadcrumbList}>
                    {root && (
                        <Node {...BreadcrumbListItem}>
                            {root} {breadcrumbLinksToRender && separator}
                        </Node>
                    )}
                    {backButton && <Node {...BreadcrumbListItem}>{backButton}</Node>}

                    {isMobile ? renderMobileLinks() : renderDesktopLinks()}
                </Node>

                <Node className='' vocab='https://schema.org/' typeof='BreadcrumbList'>
                    {result && result.length > 0
                        ? result.map((item, index) => {
                              return (
                                  <Node tag='span' key={index} property='itemListElement' typeof='ListItem' className=''>
                                      <meta property='position' content={(index + 1)?.toString()} />
                                      {item.hasOwnProperty('Name') && item.Name !== undefined && (
                                          <meta property='Name' content={item.Name.split('-').join(' ')} />
                                      )}
                                      {item.hasOwnProperty('Url') && item.Url !== undefined && (
                                          <meta property='item' content={pageUrl + item.Url.toString()} />
                                      )}
                                  </Node>
                              );
                          })
                        : null}
                </Node>
            </Module>
        );
    }

    return (
        <Module {...BreadcrumbContainer}>
            <Node {...BreadcrumbList}>
                {root && (
                    <Node {...BreadcrumbListItem}>
                        {root}
                        {links && separator}
                    </Node>
                )}
                {backButton && <Node {...BreadcrumbListItem}>{backButton}</Node>}
                {links &&
                    links.map((link, index) => {
                        return (
                            <React.Fragment key={index}>
                                <Node
                                    {...BreadcrumbListItem}
                                    className={classnames(
                                        BreadcrumbListItem.className,
                                        index === links.length - 1 ? 'breadcrumb_item-current' : ''
                                    )}
                                >
                                    {link}
                                    {links[index + 1] && separator}
                                </Node>
                            </React.Fragment>
                        );
                    })}
            </Node>

            {/* Node added for schema */}
            <Node className='' vocab='https://schema.org/' typeof='BreadcrumbList'>
                {result && result.length > 0
                    ? result.map((item, index) => {
                          return (
                              <Node tag='span' key={index} property='itemListElement' typeof='ListItem' className=''>
                                  <meta property='position' content={(index + 1)?.toString()} />
                                  {item.hasOwnProperty('Name') && item.Name !== undefined && (
                                      <meta property='Name' content={item.Name.split('-').join(' ')} />
                                  )}
                                  {item.hasOwnProperty('Url') && item.Url !== undefined && (
                                      <meta property='item' content={pageUrl + item.Url.toString()} />
                                  )}
                              </Node>
                          );
                      })
                    : null}
            </Node>
        </Module>
    );
};

export default BreadCrumbView;
